import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@nxsys/store';
import { WORKER_POLICY, WorkerPolicyKey } from '@nxsys/constants/workerPolicy';
import {
  hasPolicy,
  hasAllPolicies,
  hasAnyPolicy,
} from '@nxsys/helpers/policyHelper';

const usePolicies = () =>
  useSelector((state: RootState) => state.appStore.policies);

// Personal profile permissions
export const useIsAllowedPersonalDetailsUpdate = () => {
  const policies = usePolicies();
  return hasPolicy(policies, WORKER_POLICY.PERSONAL_DETAILS_UPDATE);
};

export const useIsAllowedContactUpdate = () => {
  const policies = usePolicies();
  return hasPolicy(policies, WORKER_POLICY.CONTACT_DETAILS_UPDATE);
};

export const useIsAllowedAddressUpdate = () => {
  const policies = usePolicies();
  return hasPolicy(policies, WORKER_POLICY.ADDRESS_UPDATE);
};

export const useIsAllowedBankDetailsUpdate = () => {
  const policies = usePolicies();
  return hasPolicy(policies, WORKER_POLICY.BANK_DETAILS_UPDATE);
};

export const useIsAllowedHolidayPayShow = () => {
  const policies = usePolicies();
  return hasPolicy(policies, WORKER_POLICY.HOLIDAY_PAY_DETAILS_SHOW);
};

// Work-related permissions
export const useIsAllowedTimesheetCreate = () => {
  const policies = usePolicies();
  return hasPolicy(policies, WORKER_POLICY.TIMESHEETS_CREATE);
};

export const useIsAllowedDocumentCreate = () => {
  const policies = usePolicies();
  return hasPolicy(policies, WORKER_POLICY.DOCUMENTS_CREATE);
};

export const useIsAllowedExpenseCreate = () => {
  const policies = usePolicies();
  return hasPolicy(policies, WORKER_POLICY.EXPENSE_CREATE);
};

// Generic hooks for checking any worker policy
export const useHasPolicy = (policy: WorkerPolicyKey) => {
  const policies = usePolicies();
  return hasPolicy(policies, policy);
};

export const useHasAllPolicies = (requiredPolicies: WorkerPolicyKey[]) => {
  const policies = usePolicies();
  return hasAllPolicies(policies, requiredPolicies);
};

export const useHasAnyPolicy = (requiredPolicies: WorkerPolicyKey[]) => {
  const policies = usePolicies();
  return hasAnyPolicy(policies, requiredPolicies);
};

// Combined hook returning all employee permission flags
export const useEmployeePermissions = () => {
  const policies = usePolicies();

  return useMemo(
    () => ({
      canUpdatePersonalDetails: hasPolicy(
        policies,
        WORKER_POLICY.PERSONAL_DETAILS_UPDATE,
      ),
      canUpdateContact: hasPolicy(
        policies,
        WORKER_POLICY.CONTACT_DETAILS_UPDATE,
      ),
      canUpdateAddress: hasPolicy(
        policies,
        WORKER_POLICY.ADDRESS_UPDATE,
      ),
      canUpdateBankDetails: hasPolicy(
        policies,
        WORKER_POLICY.BANK_DETAILS_UPDATE,
      ),
      canShowHolidayPay: hasPolicy(
        policies,
        WORKER_POLICY.HOLIDAY_PAY_DETAILS_SHOW,
      ),
      canCreateTimesheet: hasPolicy(
        policies,
        WORKER_POLICY.TIMESHEETS_CREATE,
      ),
      canCreateDocument: hasPolicy(
        policies,
        WORKER_POLICY.DOCUMENTS_CREATE,
      ),
      canCreateExpense: hasPolicy(
        policies,
        WORKER_POLICY.EXPENSE_CREATE,
      ),
    }),
    [policies],
  );
};
